// tslint:disable: no-input-rename no-output-rename

import { Input, TemplateRef, Directive, HostListener, EventEmitter, Output, OnDestroy } from '@angular/core';
import { ModalService, ModalConfig } from './modal.service';
import { Subscription } from 'rxjs';

const PRIMARY_ACTION_TAG = 'primaryAction';
const SECONDARY_ACTION_TAG = 'secondaryAction';

/**
 * Modals are overlays that prevent users from interacting with the rest of the application until a specific action is taken. They can be disruptive because they require users to take an action before they can continue interacting with the rest of the application.
 * It should be used thoughtfully and sparingly.
 */
@Directive({
  selector: '[sukaModal]'
})
export class ModalDirective {
  constructor(protected modalService: ModalService) { }

  /**
   * Set to `true` to show a close button on the top right corner. Defaults to `false`.
   */
  @Input('modalCloseButton') closeButton = false;
  /**
   * Sets the modal title.
   */
  @Input('modalTitle') title: string | TemplateRef<any>;
  /**
   * Sets the body of the modal.
   */
  @Input('modalBody') body: string | TemplateRef<any>;
  /**
   * Sets to `false` to stop the modal from closing when the user clicks on the backdrop. Defaults to `true`.
   */
  @Input('modalCloseOnBackdropClick') closeOnBackdropClick = true;

  /**
   * Callback when the modal closes.
   */
  @Output('modalClose') close = new EventEmitter();

  /**
   * Sets the modal secondary action button label.
   */
  @Input('modalSecondaryActionLabel') secondaryActionLabel: string;
  /**
   * Callback when the secondary action is triggered.
   */
  @Output('modalSecondaryAction') secondaryAction = new EventEmitter();

  /**
   * Sets the modal primary action button label.
   */
  @Input('modalPrimaryActionLabel') primaryActionLabel: string;
  /**
   * Callback when the primary action is triggered.
   */
  @Output('modalPrimaryAction') primaryAction = new EventEmitter();

  @HostListener('click')
  onClick() {
    this.openModal();
  }

  /**
   * Opens the modal.
   */
  private openModal() {
    const modalConfig: ModalConfig = {
      closeOnBackdropClick: this.closeOnBackdropClick,
      content: {
        closeButton: this.closeButton,
        title: this.title,
        body: this.body,
      }
    };

    if (this.primaryActionLabel || this.secondaryActionLabel) {
      modalConfig.content.actions = [];

      if (this.primaryActionLabel) {
        modalConfig.content.actions.push({
          label: this.primaryActionLabel,
          type: 'primary',
          tag: PRIMARY_ACTION_TAG
        });
      }

      if (this.secondaryActionLabel) {
        modalConfig.content.actions.push({
          label: this.secondaryActionLabel,
          type: 'secondary',
          tag: SECONDARY_ACTION_TAG
        });
      }
    }

    const modalRef = this.modalService.open(modalConfig);

    modalRef.afterClosed().subscribe((result: any) => {
      switch (result) {
        case PRIMARY_ACTION_TAG:
          if (this.primaryAction.observers.length > 0) {
            this.primaryAction.emit(result);
          }
          break;
        case SECONDARY_ACTION_TAG:
          if (this.secondaryAction.observers.length > 0) {
            this.secondaryAction.emit(result);
          }
          break;
        default:
          if (this.close.observers.length > 0) {
            this.close.emit();
          }
          break;
      }
    });
  }
}
